import React, { useMemo, useContext, Fragment } from "react";
import devTranslations from "../languages/device.json";
import { LanguageContext } from "../context/LanguageContext";
import { useAppTheme } from "../context/ThemeContext";
import { getThemeColorValue, normalizeThemeMode } from "../utils/themeColors";

import { CheckCircle, ThumbUp, Cancel } from "@mui/icons-material";
import Handshake from "@mui/icons-material/Handshake";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { capitalize } from "@mui/material";

import "../Components/modal.css";
import "./DataEvaluationDetails.css";

const StatusIndicator = ({ statusKey, displayName, t, color: colorOverride }) => {
  const key = String(statusKey ?? "").toLowerCase();

  let Icon = InfoOutlined;
  let color = "var(--primary)";

  if (
    key.includes("perfect") ||
    key.includes("excellent") ||
    key.includes("perfecto") ||
    key.includes("ممتاز")
  ) {
    Icon = CheckCircle;
    color = "#4caf50";
  } else if (
    key.includes("very good") ||
    (key.includes("good") && !key.includes("very")) ||
    key.includes("جيد")
  ) {
    Icon = ThumbUp;
    color = "#8bc34a";
  } else if (key.includes("acceptable") || key.includes("ok") || key.includes("مقبول")) {
    Icon = Handshake;
    color = "#ff9800";
  } else if (key.includes("bad") || key.includes("poor") || key.includes("سيء")) {
    Icon = Cancel;
    color = "#f44336";
  }

  const safeOverride =
    typeof colorOverride === "string" && colorOverride.trim() ? colorOverride.trim() : null;
  if (safeOverride) color = safeOverride;

  return (
    <div className="eval-status">
      <Icon className="eval-status-icon" style={{ color }} />
      <span className="eval-status-text" style={{ color }}>
        {t?.[key] || displayName || capitalize(key) || "—"}
      </span>
    </div>
  );
};

const DataEvaluationDetails = ({ state, setState, template, latestStreamData, allEvaluations }) => {
  const { lang } = useContext(LanguageContext);
  const { mode } = useAppTheme();
  const themeMode = normalizeThemeMode(mode);
  const L = devTranslations?.[lang]?.evaluation || {};
  const isRtl = ["ar", "ur"].includes(lang);

  const onClose = () => setState({ open: false });

  const evalData = useMemo(() => {
    if (allEvaluations?.evaluation_names || allEvaluations?.datastream_ranges) return allEvaluations;
    if (allEvaluations?.templateEvaluationData) return allEvaluations.templateEvaluationData;
    return { evaluation_names: [], datastream_ranges: [] };
  }, [allEvaluations]);

  const evaluationNames = useMemo(
    () => (Array.isArray(evalData?.evaluation_names) ? evalData.evaluation_names : []),
    [evalData]
  );

  const ranges = useMemo(
    () => (Array.isArray(evalData?.datastream_ranges) ? evalData.datastream_ranges : []),
    [evalData]
  );

  const evalByTemplateId = useMemo(() => {
    const map = new Map();
    evaluationNames.forEach((e) => {
      const id = e?.template_datastream_evaluation_id;
      if (id == null) return;
      const resolved = getThemeColorValue(
        { ...e, colorLight: e?.colorLight ?? e?.color_light },
        "color",
        themeMode,
        null
      );
      map.set(String(id), {
        name: e?.name ?? "Unknown",
        priority: e?.priority != null ? Number(e.priority) : 999,
        color: resolved,
      });
    });
    return map;
  }, [evaluationNames, themeMode]);

  const formatRange = (from, to, unit) => {
    const u = unit != null && String(unit).trim() !== "" ? ` ${unit}` : "";
    const f = from != null && !Number.isNaN(Number(from)) ? Number(from) : null;
    const t = to != null && !Number.isNaN(Number(to)) ? Number(to) : null;

    if (f == null && t == null) return `—`;
    if (f != null && t != null) return `${f} → ${t}${u}`;
    if (f != null) return `≥ ${f}${u}`;
    return `≤ ${t}${u}`;
  };

  const getEvaluationForValue = (value, stream) => {
    const val = Number(value);
    if (!stream?.id || Number.isNaN(val)) return null;
    if (!Array.isArray(ranges) || ranges.length === 0) return null;

    const streamRanges = ranges.filter((r) => String(r?.datastream_id) === String(stream.id));
    if (streamRanges.length === 0) return null;

    const combined = streamRanges
      .map((r) => {
        const tid = r?.template_datastream_evaluation_id;
        const meta = tid != null ? evalByTemplateId.get(String(tid)) : null;

        return {
          from: r?.from_value != null && !Number.isNaN(Number(r.from_value)) ? Number(r.from_value) : null,
          to: r?.to_value != null && !Number.isNaN(Number(r.to_value)) ? Number(r.to_value) : null,
          name: meta?.name ?? "Unknown",
          priority: meta?.priority ?? 999,
          color: meta?.color ?? null,
        };
      })
      .filter((x) => x.from != null || x.to != null);

    if (combined.length === 0) return null;

    
    combined.sort((a, b) => a.priority - b.priority);

    for (const r of combined) {
      const okFrom = r.from == null || val >= r.from;
      const okTo = r.to == null || val <= r.to;
      if (okFrom && okTo) {
        return {
          name: r.name,
          priority: r.priority,
          color: r.color ?? null,
          from: r.from,
          to: r.to,
          outOfRange: false,
        };
      }
    }

    const numericFroms = combined.map((r) => r.from).filter((x) => x != null);
    const numericTos = combined.map((r) => r.to).filter((x) => x != null);

    const expectedFrom = numericFroms.length ? Math.min(...numericFroms) : null;
    const expectedTo = numericTos.length ? Math.max(...numericTos) : null;

    return {
      name: "Bad",
      priority: 999,
      color: "#f44336",
      from: null,
      to: null,
      outOfRange: true,
      expectedFrom,
      expectedTo,
    };
  };

  const datastreamCards = useMemo(() => {
    if (!template || !latestStreamData) return [];

    const cards = [];

    Object.entries(latestStreamData).forEach(([pin, value]) => {
      const stream = template?.datastreams?.find((ds) => String(ds.pin) === String(pin));
      const val = Number(value);
      if (!stream || stream.id == null || Number.isNaN(val)) return;

      const evalResult = getEvaluationForValue(val, stream);
      if (!evalResult) return; 

      cards.push({
        id: String(stream.id),
        pin: String(pin),
        name: stream?.name || `PIN ${pin}`,
        unit: stream?.unit ?? null,
        latest: val,
        evaluation: evalResult,
      });
    });

    cards.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return cards;
  }, [template, latestStreamData, ranges, evalByTemplateId]);

  if (!state?.open) return null;

  return (
    <Fragment>
      <div id="modal" onClick={onClose}>
        <div
          id="popup"
          className={`dashboard-modal data-eval-modal ${isRtl ? "rtl" : ""}`}
          onClick={(e) => e.stopPropagation()}
          dir={isRtl ? "rtl" : "ltr"}
        >
          <div className="dashboard-header">
            <div className="dashboard-header-text">
              <h2>{L.dataEvaluation || "Data Evaluation"}</h2>
              <p>
                {L.viewModeText || "You are in a "}
                <strong>{L.viewMode || "View"}</strong>
                {L.viewModeText2 || " mode. This data is read-only."}
              </p>
            </div>
          </div>

          <div className="modal-divider" />

          <div className="modal-content">
            <div className="dashboard-content data-eval-content">
              <div className="modal-info data-eval-info-banner">
                <div className="data-eval-info-icon">i</div>
                <div className="data-eval-info-text">
                  <h4>{L.summaryTitle || "Summary"}</h4>
                  <p>
                    {L.summaryDesc ||
                      "Only streams with evaluations are shown. Each stream shows ONLY the range that produced its current evaluation."}
                  </p>
                </div>
              </div>

              {datastreamCards.length === 0 ? (
                <div className="data-eval-empty">{L.noData || "—"}</div>
              ) : (
                <div className="data-eval-sections">
                  {datastreamCards.map((ds) => (
                    <div className="data-eval-section" key={ds.id}>
                      <div className="data-eval-section-header">
                        <div className="data-eval-ds-header">
                          <div className="data-eval-ds-title">
                            <div className="data-eval-ds-name-row">
                              <span className="data-eval-ds-name">{ds.name}</span>
                              <span className="data-eval-ds-pin"> : {ds.pin}</span>
                            </div>
                            <div className="data-eval-ds-latest-row">
                              <span className="data-eval-ds-latest-label">
                                {L.latestValue || "Latest"} :
                              </span>
                              <span className="data-eval-ds-latest-value">
                                {String(ds.latest)}
                                {ds.unit != null && String(ds.unit).trim() !== ""
                                  ? ` ${String(ds.unit).trim()}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="data-eval-grid data-eval-grid-ranges">
                        <div className="data-eval-item data-eval-item-range">
                          <StatusIndicator
                            statusKey={String(ds.evaluation?.name || "").toLowerCase()}
                            displayName={ds.evaluation?.name}
                            t={L}
                            color={ds.evaluation?.color}
                          />
                          <span className="data-eval-range-text">
                            {ds.evaluation?.outOfRange ? (
                              <span>
                                <span style={{ display: "block" }}>
                                  {L.outOfRange || "Out of defined ranges"}
                                </span>
                                <span style={{ display: "block", opacity: 0.85 }}>
                                  {L.expectedRange || "Expected"} :{" "}
                                  {formatRange(
                                    ds.evaluation?.expectedFrom,
                                    ds.evaluation?.expectedTo,
                                    ds.unit
                                  )}
                                </span>
                              </span>
                            ) : (
                              formatRange(ds.evaluation?.from, ds.evaluation?.to, ds.unit)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer data-eval-footer">
            <div className="data-eval-footer-right">
              <button className="btn btn-cancel" onClick={onClose}>
                {L.closeBtn || "Close"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DataEvaluationDetails;
